import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MatBottomSheet, MatBottomSheetRef } from '@angular/material/bottom-sheet';
import { MatMenuTrigger } from '@angular/material/menu';

import { Feature } from 'src/app/services/acl.service';
import { AuthService } from 'src/app/services/auth.service';
import { FLAGS, FlagsService } from 'src/app/services/flags.service';
import { QuestionsService } from 'src/app/services/questions.service';
import { ScreenSizeService } from 'src/app/services/screen-size.service';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import {
  ColorScheme,
  PencilBottomSheetContainerComponent,
} from 'src/app/standalones/components/pencil-bottom-sheet-container/pencil-bottom-sheet-container.component';
import {
  Course,
  IndividualUserOverview,
  Institution,
  OnlineStatus,
  UserRole,
} from '../../models/user';
import { SettingsService } from '../../services/settings.service';
import { UserService } from '../../services/user.service';
import { CustomerSupportService } from '../../services/customer-support.service';
import { overlayDialogRef } from '../../utilities/bottom-sheet.utils';

@UntilDestroy()
@Component({
  selector: 'app-profile-photo',
  templateUrl: './profile-photo.component.html',
  styleUrls: ['./profile-photo.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfilePhotoComponent implements OnInit {
  @Input() user: IndividualUserOverview | undefined;
  @Input() width?: number;
  @Input() fontSize?: number;
  @Input() fontWeight?: number;
  @Input() fontColor?: string;
  @Input() showStatus?: boolean;
  @Input() userStatus?: OnlineStatus;
  @Input() fromQuestionList?: boolean;
  @Input() isClickEnabled = true;
  @Input() opaqueOverlay = false;
  @Input() institution?: Institution; // When provided, institution name and image will be shown if !user
  @Input() showName = false;
  @Input() showHostTag = false;
  @Input() showYouWord = false;
  @Input() showSettings = true;
  @Input() hideMatTooltip = false;
  @Input() hasMenu = false;
  @Input() imgScale = 1;
  @Input() enableImageBorder = false;
  @Input() parentDisplay?: string;
  @Input() imgBorder = false;
  @Input() showAnimatedImageBorder = false;
  @ViewChild('profileMenuTrigger')
  profileMenuTrigger?: MatMenuTrigger;
  @ViewChild('bottomSheetContainer')
  bottomSheetContainer?: TemplateRef<PencilBottomSheetContainerComponent>;

  OnlineStatusEnum = OnlineStatus;
  UserRoleEnum = UserRole;
  courses: Course[] = [];
  langSelectionEnabled = false;
  Features = Feature;
  isDesktop = true;
  isRTL = false;
  readonly ColorScheme = ColorScheme;
  private profileBottomSheetRef?: MatBottomSheetRef;

  constructor(
    public router: Router,
    public userService: UserService,
    public questionsService: QuestionsService,
    public authService: AuthService,
    private flagsService: FlagsService,
    private settingsService: SettingsService,
    private screenSizeService: ScreenSizeService,
    private bottomSheet: MatBottomSheet,
    private customerSupportService: CustomerSupportService,
    private cdr: ChangeDetectorRef,
  ) {
    if (this.flagsService.isFlagEnabled(FLAGS.SPACES_LANGUAGE_SELECTOR)) {
      this.langSelectionEnabled = true;
    }
    this.screenSizeService.isDesktop$.pipe(untilDestroyed(this)).subscribe((isDesktop: boolean) => {
      this.isDesktop = isDesktop;
      this.closeProfileMenu();
      this.cdr.markForCheck();
    });
    this.userService.rtl.pipe(untilDestroyed(this)).subscribe((res) => {
      this.isRTL = res;
    });
  }

  ngOnInit(): void {
    if (typeof this.user === 'string') {
      this.userService
        .getIndividualUserStats(this.user)
        .pipe(untilDestroyed(this))
        .subscribe((res) => {
          this.user = res;
        });
    }

    if (!this.user && !this.fromQuestionList) {
      this.userService.user.pipe(untilDestroyed(this)).subscribe((res: any) => {
        this.user = res && res.user;
      });
    }
  }

  profileClicked(): void {
    if (this.user && this.isClickEnabled) {
      this.router.navigate(['profiles', this.user._id]);
    }
  }

  openProfileBottomSheet(): void {
    if (!this.hasMenu || this.isDesktop || !this.bottomSheetContainer) {
      return;
    }

    this.profileBottomSheetRef = this.bottomSheet.open(this.bottomSheetContainer, {
      panelClass: 'pencil-bottom-sheet',
      closeOnNavigation: true,
    });
  }

  closeProfileMenu(skipAnimation = false): void {
    if (skipAnimation) {
      overlayDialogRef(this.profileBottomSheetRef)?.close();
    } else {
      this.profileBottomSheetRef?.dismiss();
    }
    this.profileBottomSheetRef = undefined;
    this.profileMenuTrigger?.closeMenu();
  }

  openHelp(): void {
    this.closeProfileMenu();
    this.customerSupportService.openSupport();
  }

  openSettingsPanel(): void {
    this.closeProfileMenu(true);
    this.settingsService.showSettingsPanel(undefined, true);
  }

  signOut(): void {
    this.closeProfileMenu();
    this.authService.showLogoutConfirmationDialog();
  }

  protected readonly IconTypes = IconTypes;
}
